import { createContext, useContext, useEffect, useState, type PropsWithChildren } from 'react';

interface LegalProviderProps extends PropsWithChildren {
    localStorageKey: string;
}

interface LegalContextType {
    hasAgreed: boolean | undefined;
    setAgreed: (_: boolean) => void;
}

const LegalContext = createContext<LegalContextType | null>(null);

const LegalProvider = ({ localStorageKey, children }: LegalProviderProps) => {
    // we use undefined here to deal with unknown state -- before useEffect runs we aren't sure
    // so we track the third state of unknown.
    const [hasAgreed, setHasAgreed] = useState<LegalContextType['hasAgreed']>(undefined);

    useEffect(() => {
        const item = window.localStorage.getItem(localStorageKey);
        let hasAgreedValue = false;

        if (item) {
            hasAgreedValue = (JSON.parse(item) || false) as boolean;
        }

        // Reading from localStorage can only happen in an effect (not during SSR)
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setHasAgreed(hasAgreedValue);
    }, [setHasAgreed, localStorageKey]);

    const setAgreedState = (newHasAgreed: boolean) => {
        window.localStorage.setItem(localStorageKey, JSON.stringify(newHasAgreed));
        setHasAgreed(newHasAgreed);
    };

    return (
        <LegalContext.Provider value={{ hasAgreed, setAgreed: setAgreedState }}>
            {children}
        </LegalContext.Provider>
    );
};

const useLegalTerms = () => {
    const context = useContext(LegalContext);
    if (!context) {
        throw new Error('`useLegalTerms()` must be used inside a `LegalProvider`');
    }
    return context;
};

export { LegalProvider, useLegalTerms };
